/**
 * ScrollMemoryService - Manages scroll position memory for dashboard tiles
 * Uses localStorage to persist scroll positions across page reloads
 */

const STORAGE_KEY = 'checkboxr_scroll_memory';
const CLEANUP_INTERVAL = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

class ScrollMemoryService {
  constructor() {
    this.cleanupOldEntries();
  }

  /**
   * Get all scroll memory data from localStorage
   */
  getScrollMemory() {
    try {
      const data = localStorage.getItem(STORAGE_KEY);
      return data ? JSON.parse(data) : {};
    } catch (error) {
      console.warn('Failed to read scroll memory from localStorage:', error);
      return {};
    }
  }

  /**
   * Save scroll memory data to localStorage
   */
  saveScrollMemory(data) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch (error) {
      console.warn('Failed to save scroll memory to localStorage:', error);
    }
  }

  /**
   * Get scroll position for a specific list
   */
  getListScrollPosition(listId) {
    const memory = this.getScrollMemory();
    return memory[listId] || null;
  }

  /**
   * Set scroll position for a specific list
   */
  setListScrollPosition(listId, scrollData) {
    const memory = this.getScrollMemory();
    memory[listId] = {
      ...scrollData,
      timestamp: Date.now()
    };
    this.saveScrollMemory(memory);
  }

  /**
   * Calculate which tasks should be visible based on scroll position
   */
  calculateVisibleTaskRange(scrollTop, taskHeight, availableHeight, totalTasks, defaultMaxTasks) {
    // Input validation
    if (totalTasks <= 0 || taskHeight <= 0 || defaultMaxTasks <= 0) {
      return { start: 0, end: 0 };
    }

    if (scrollTop <= 0) {
      return { start: 0, end: Math.min(defaultMaxTasks, totalTasks) };
    }

    const tasksAboveView = Math.floor(scrollTop / taskHeight);
    const visibleTaskCount = Math.ceil(Math.max(availableHeight, 0) / taskHeight);
    
    const start = Math.max(0, Math.min(tasksAboveView, totalTasks));
    const end = Math.min(totalTasks, start + Math.max(visibleTaskCount, defaultMaxTasks));

    // Ensure start is never greater than end
    return { start: Math.min(start, end), end };
  }

  /**
   * Calculate scroll position based on task range
   */
  calculateScrollFromTaskRange(startIndex, taskHeight) {
    return startIndex * taskHeight;
  }

  /**
   * Check if content needs expansion based on task count and available space
   */
  needsExpansion(taskCount, availableHeight, taskHeight) {
    if (taskCount <= 0 || availableHeight <= 0 || taskHeight <= 0) {
      return false;
    }
    const maxVisibleTasks = Math.floor(availableHeight / taskHeight);
    return taskCount > maxVisibleTasks;
  }

  /**
   * Clean up old scroll memory entries (older than CLEANUP_INTERVAL)
   */
  cleanupOldEntries() {
    const memory = this.getScrollMemory();
    const cutoffTime = Date.now() - CLEANUP_INTERVAL;
    let hasChanges = false;

    Object.keys(memory).forEach(listId => {
      if (memory[listId].timestamp < cutoffTime) {
        delete memory[listId];
        hasChanges = true;
      }
    });

    if (hasChanges) {
      this.saveScrollMemory(memory);
    }
  }

  /**
   * Remove scroll memory for a specific list
   */
  clearListScrollPosition(listId) {
    const memory = this.getScrollMemory();
    if (memory[listId]) {
      delete memory[listId];
      this.saveScrollMemory(memory);
    }
  }

  /**
   * Clear all scroll memory (for testing or user preference)
   */
  clearAllScrollMemory() {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      console.warn('Failed to clear scroll memory:', error);
    }
  }
}

// Export singleton instance
export const scrollMemoryService = new ScrollMemoryService();
export default scrollMemoryService;